import { useQuery } from '@tanstack/react-query'
import { categoryApi } from '@/services/api'

interface CategorySelectProps {
  selected: string[]
  onChange: (categories: string[]) => void
  required?: boolean
  disabled?: boolean
}

export default function CategorySelect({ selected, onChange, required = false, disabled = false }: CategorySelectProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryApi.list(),
  })

  const categories = data?.data?.categories || []

  const handleToggle = (category: string) => {
    if (selected.includes(category)) {
      onChange(selected.filter(c => c !== category))
    } else {
      onChange([...selected, category])
    }
  }

  const handleSelectAll = () => {
    onChange(categories)
  }

  const handleDeselectAll = () => {
    onChange([])
  }

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading categories...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <label className="label">
          Categories (USPTO Classes) {required && <span className="text-red-500">*</span>}
        </label>
        <div className="space-x-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={disabled}
          >
            Select All
          </button>
          <button
            type="button"
            onClick={handleDeselectAll}
            className="text-sm text-gray-600 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={disabled}
          >
            Deselect All
          </button>
        </div>
      </div>

      <div className="border border-gray-300 rounded-lg p-2 max-h-60 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
          {categories.map((category: string) => (
            <label
              key={category}
              className={`flex items-center space-x-2 px-2 py-1 rounded ${
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={selected.includes(category)}
                onChange={() => handleToggle(category)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                disabled={disabled}
              />
              <span className="text-sm text-gray-700">{category}</span>
            </label>
          ))}
        </div>
      </div>

      <p className="mt-2 text-sm text-gray-500">
        {selected.length} {selected.length === 1 ? 'category' : 'categories'} selected
      </p>
    </div>
  )
}
